import React from "react";
import { useFormikContext } from "formik";
import classNames from "classnames";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Taxonomy } from "@common/categories";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { useCategoriesTree } from "@hooks/useCategoriesTree";
import { v4 as uuid } from "uuid";
import {
    defaultCategoryTaxonomy,
    TransferMatchingMode,
    TransferRuleFormSchema,
} from "../constants";
import { FormRuleActionType } from "../../TransactionRuleForm/constants";
import styles from "../TransferRuleForm.module.scss";

const matchingModeOptions: Array<{
    value: TransferMatchingMode;
    label: string;
}> = [
    { value: "allow", label: "Allow matching" },
    { value: "disallow", label: "Disallow matching" },
];

export const MatchingModeSelect: React.FC = () => {
    const { values, setFieldValue } =
        useFormikContext<TransferRuleFormSchema>();
    const categories = useCategoriesTree();

    const buildDefaultAllowActions = (): TransferRuleFormSchema["actions"] => {
        const defaultCategories = categories
            .filter((c) => defaultCategoryTaxonomy.includes(c.taxonomy))
            .filter(({ id }) => id);
        const defaultCategory = defaultCategories.find(
            (c) => c.taxonomy === Taxonomy.transfer,
        );

        return {
            from: [
                {
                    id: uuid(),
                    actionType: FormRuleActionType.CATEGORY,
                    value: { categoryId: defaultCategory?.id },
                    transferDirection: TransferRuleDirection.FROM,
                },
            ],
            to: [
                {
                    id: uuid(),
                    actionType: FormRuleActionType.CATEGORY,
                    value: { categoryId: defaultCategory?.id },
                    transferDirection: TransferRuleDirection.TO,
                },
            ],
        };
    };

    return (
        <section
            className={classNames(styles.actions, styles.matchingModeSection)}
        >
            <main className={styles.content}>
                <div className={styles.sectionRows}>
                    <div className={styles.sectionRow}>
                        <div className={styles.sectionRowContent}>
                            <span className={styles.matchingModeLabel}>
                                Mode
                            </span>
                            <Picker.Select
                                items={matchingModeOptions}
                                value={values.matchingMode}
                                onValueChange={(v) => {
                                    if (
                                        v === null ||
                                        v === values.matchingMode
                                    ) {
                                        return;
                                    }

                                    void setFieldValue("matchingMode", v);
                                    void setFieldValue(
                                        "includedTransactionsIds",
                                        [],
                                    );
                                    void setFieldValue(
                                        "excludedTransactionsIds",
                                        [],
                                    );

                                    const isSwitchingToAllowWithEmptyActions =
                                        v === "allow" &&
                                        values.actions.from.length === 0 &&
                                        values.actions.to.length === 0;

                                    if (isSwitchingToAllowWithEmptyActions) {
                                        void setFieldValue(
                                            "actions",
                                            buildDefaultAllowActions(),
                                        );
                                    }
                                }}
                                placeholder="Select mode"
                            />
                        </div>
                    </div>
                </div>
            </main>
        </section>
    );
};
